import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'
import {
  IActionType,
  VARIABLE_TEXT_FEATURE_FLAG_ALL,
  VARIABLE_TEXT_FEATURE_FLAG_NO_ICON,
  VARIABLE_TEXT_FEATURE_FLAG_TEXT,
  VariableSelectFeatureFlag,
  VariableTextFeatureFlag,
} from '@makyoapp/makyo-common'

import {
  AspectRatio,
  BuilderComponentGroup,
  BuilderComponentSettingType,
  BuilderComponentSettingValidation,
  ComponentSettingKey,
  getRatioLabel,
  MdGalleryLayout,
  MdShape,
  SpacingInputUnit,
} from '../types'
import { LABELS } from '../types/labels'
import {
  hoverEffectOptions,
  makeClampWidthItems,
  makeNumItems,
  makeSpacing,
  makeTypographyGroup,
} from '../utils/components'
import { formatResponsiveSettingValues } from '../viewport/responsive-settings'

export const MediaComponents: BuilderComponentGroup = {
  id: 'media',
  title: 'Media',
  components: [
    // image
    {
      compId: 'image',
      title: LABELS.IMAGE_KEY,
      searchKeywords: ['image', 'picture', 'photo', 'banner', 'hero image', 'illustration', 'static image'],
      icon: 'Image16',
      nested: false,
      settings: {
        data: [
          {
            id: ComponentSettingKey.ImageDataSrc,
            type: BuilderComponentSettingType.VariableSelect,
            label: LABELS.IMAGE_KEY,
            variableSelectSettings: {
              flags:
                VariableSelectFeatureFlag.ColumnData |
                VariableSelectFeatureFlag.SingleCell |
                VariableSelectFeatureFlag.CurrentRow |
                VariableSelectFeatureFlag.ProfileData |
                VariableSelectFeatureFlag.Image |
                VariableSelectFeatureFlag.PageCover,
              columnTypes: [
                ColumnTypes.Image,
                ColumnTypes.URL,
                ColumnTypes.Lookup,
                ColumnTypes.QRCode,
                ColumnTypes.Barcode,
              ],
              onValueChange: (nextVal, prevSettings, type) => {
                switch (type) {
                  case ColumnTypes.Barcode:
                    prevSettings[ComponentSettingKey.ImageDesignRatio] = formatResponsiveSettingValues(
                      prevSettings[ComponentSettingKey.ImageDesignRatio],
                      AspectRatio.Auto
                    )
                    break
                  case ColumnTypes.QRCode:
                    prevSettings[ComponentSettingKey.ImageDesignRatio] = formatResponsiveSettingValues(
                      prevSettings[ComponentSettingKey.ImageDesignRatio],
                      AspectRatio.OneByOne
                    )
                    break
                  default:
                    break
                }
              },
            },
            defaultValue: {
              flag: VariableSelectFeatureFlag.Image,
              value: 'https://storage.makyo.me/assets/default_thumbnail_2.png',
            },
          },
          {
            id: ComponentSettingKey.ImageDataTitle,
            type: BuilderComponentSettingType.VariableText,
            label: LABELS.TITLE_KEY,
            variableTextSettings: {
              flags: VARIABLE_TEXT_FEATURE_FLAG_TEXT,
              // flags: VARIABLE_TEXT_FEATURE_FLAG_TEXT_NO_PAGE_TITLE,
            },
          },
          {
            id: ComponentSettingKey.ImageDataLike,
            type: BuilderComponentSettingType.VariableSelect,
            label: LABELS.LIKES_KEY,
            variableSelectSettings: {
              flags:
                VariableSelectFeatureFlag.ColumnData |
                VariableSelectFeatureFlag.SingleCell |
                VariableSelectFeatureFlag.ProfileData,
              columnTypes: [ColumnTypes.Boolean, ColumnTypes.Function],

              //inputType: 'number'
            },
          },
          /*{
            id: ComponentSettingKey.ImageDataBadge,
            type: BuilderComponentSettingType.VariableSelect,
            label: 'Badge',
            variableSelectSettings: {
              flags:
                VariableSelectFeatureFlag.ColumnData |
                VariableSelectFeatureFlag.SingleCell |
                VariableSelectFeatureFlag.CustomText,
              columnTypes: [ColumnTypes.Number],
              inputType: 'number'
            }
          },*/
          {
            id: ComponentSettingKey.ImageDataTag,
            type: BuilderComponentSettingType.VariableText,
            label: LABELS.BADGE_KEY,
            variableTextSettings: {
              flags: VARIABLE_TEXT_FEATURE_FLAG_TEXT,
              // flags: VARIABLE_TEXT_FEATURE_FLAG_TEXT_NO_PAGE_TITLE,
            },
          },
        ],
        designGroup: {
          [LABELS.COLORS_KEY]: {
            items: [
              // makeScheme(ComponentSettingKey.ImageDesignScheme),
              // {
              //   id: ComponentSettingKey.ImageDesignComponentTitleColor,
              //   type: BuilderComponentSettingType.ColorVariant,
              //   defaultValue: 'on-primary',
              //   label: LABELS.COMPONENT_TITLE_KEY,
              //   colorVariantSettings: {
              //     schemeId: ComponentSettingKey.ImageDesignScheme,
              //     fullColor: 'fg',
              //   },
              // },
              {
                id: ComponentSettingKey.ImageDesignBorderColor,
                type: BuilderComponentSettingType.ColorVariant,
                defaultValue: 'primary-80',
                label: LABELS.BORDER_COLOR_KEY,
                colorVariantSettings: {
                  schemeId: ComponentSettingKey.ImageDesignScheme,
                  fullColor: 'bg',
                },
              },
              {
                id: ComponentSettingKey.ImageDesignBadgeColor,
                type: BuilderComponentSettingType.ColorVariant,
                defaultValue: 'primary-20',
                label: LABELS.BADGE_COLOR_KEY,
                colorVariantSettings: {
                  schemeId: ComponentSettingKey.ImageDesignScheme,
                  fullColor: 'bg',
                },
              },
              // {
              //   id: ComponentSettingKey.ImageDesignBackgroundCaption,
              //   type: BuilderComponentSettingType.Switch,
              //   defaultValue: true,
              //   label: LABELS.BACKGROUND_CAPTION_KEY,
              //   colorVariantSettings: {
              //     schemeId: ComponentSettingKey.ImageDesignScheme,
              //     fullColor: 'bg',
              //   },
              // },
            ],
          },
          [LABELS.DIMENSION_KEY]: {
            items: [
              {
                type: BuilderComponentSettingType.Slider,
                label: LABELS.WIDTH_KEY,
                id: ComponentSettingKey.ImageDesignWidth,
                defaultValue: [100],
                clampInAllDevices: true,
                sliderSettings: {
                  max: 3890,
                  min: 10,
                  step: 1,
                  range: false,
                  unit: '%',
                  showUnit: true,
                },
              },
            ],
          },
          Spacing: makeSpacing({
            margin: {
              id: ComponentSettingKey.ImageDesignMargin,
              defaultValue: { x: 20, y: 10 },
            },
          }),
          [LABELS.ALIGNMENT_KEY]: {
            items: [
              {
                id: ComponentSettingKey.ImageDesignHAlign,
                type: BuilderComponentSettingType.ComponentAlignment,
                defaultValue: 'parent',
                label: LABELS.COMPONENT_ALIGNMENT_KEY,
              },
              {
                id: ComponentSettingKey.ImageDesignCompTitleAlign,
                type: BuilderComponentSettingType.HorizontalAlignment,
                defaultValue: 'center',
                label: LABELS.COMPONENT_TITLE_ALIGNMENT_KEY,
              },
            ],
          },
          [LABELS.LIKE_KEY]: {
            items: [
              {
                id: ComponentSettingKey.ImageDesignLikeIcon,
                type: BuilderComponentSettingType.Icon,
                label: 'Icon',
                defaultValue: 'star',
              },
            ],
          },
          [LABELS.TEXT_LINES_KEY]: {
            items: [
              {
                id: ComponentSettingKey.ImageDesignTextLineCaption,
                type: BuilderComponentSettingType.Slider,
                defaultValue: [3],
                label: 'Title text lines',
                sliderSettings: {
                  max: 5,
                  min: 1,
                  step: 1,
                  range: false,
                },
              },
            ],
          },
          [LABELS.BORDER_KEY]: {
            items: [
              {
                id: ComponentSettingKey.ImageDesignBorder,
                type: BuilderComponentSettingType.Slider,
                label: LABELS.WIDTH_KEY,
                defaultValue: [0],
                sliderSettings: {
                  max: 25,
                  min: 0,
                  step: 1,
                  range: false,
                },
              },
            ],
          },
          [LABELS.CORNER_RADIUS_KEY]: {
            items: [
              {
                id: ComponentSettingKey.ImageDesignShape,
                label: '',
                type: BuilderComponentSettingType.Select,
                defaultValue: MdShape.Medium,
                selectSettings: {
                  options: Object.entries(MdShape).map(([label, value]) => ({
                    value,
                    label: label.replace(/([a-z])([A-Z])/g, '$1 $2'),
                  })),
                },
              },
            ],
          },
          [LABELS.TYPOGRAPHY_KEY]: makeTypographyGroup(ComponentSettingKey.ImageDesignTypography, [
            {
              id: ComponentSettingKey.ImageDesignCaptionTypography,
              label: 'Title',
              defaultValue: 'Body Small',
            },
            {
              id: ComponentSettingKey.ImageDesignTagTypography,
              label: 'Badge',
              defaultValue: 'Body Small',
            },
          ]),
          /*
          'Corner Radius': {
            items: [
              {
                id: ComponentSettingKey.ImageDesignShape,
                type: BuilderComponentSettingType.Radius,
                label: '',
                defaultValue: {
                  [RadiusInputPosition.StartStart]: {
                    value: 12,
                    unit: SpacingInputUnit.Pixel
                  },
                  [RadiusInputPosition.StartEnd]: {
                    value: 12,
                    unit: SpacingInputUnit.Pixel
                  },
                  [RadiusInputPosition.EndStart]: {
                    value: 12,
                    unit: SpacingInputUnit.Pixel
                  },
                  [RadiusInputPosition.EndEnd]: {
                    value: 12,
                    unit: SpacingInputUnit.Pixel
                  }
                }
              }
            ]
          },*/
          [LABELS.ASPECT_RATIO_KEY]: {
            items: [
              {
                id: ComponentSettingKey.ImageDesignRatio,
                type: BuilderComponentSettingType.Select,
                label: '',
                defaultValue: AspectRatio.OneByOne,
                selectSettings: {
                  options: Object.values(AspectRatio).map((v) => {
                    return {
                      value: v,
                      label: getRatioLabel(v),
                      icon: 'AspectAuto16',
                    }
                  }),
                },
              },
            ],
          },
          [LABELS.HOVER_EFFECT_KEY]: {
            items: [
              {
                id: ComponentSettingKey.ImageDesignHoverEffect,
                type: BuilderComponentSettingType.Select,
                label: LABELS.HOVER_EFFECT_KEY,
                selectSettings: {
                  options: hoverEffectOptions,
                },
              },
            ],
          },
        },
        action: [
          {
            id: ComponentSettingKey.ImageActionClick,
            label: LABELS.ON_CLICK_KEY,
            isSimple: false,
          },
          {
            id: ComponentSettingKey.ImageActionLikeClick,
            label: LABELS.ON_LIKE_BUTTON_CLICK_KEY,
            isSimple: false,
          },
        ],
        behavior: {
          visibility: true,
        },
      },
    },

    // video
    {
      compId: 'video',
      title: LABELS.VIDEO_KEY,
      searchKeywords: ['video', 'player', 'embed', 'mp4', 'youtube', 'vimeo', 'media'],
      icon: 'Video24',
      nested: false,
      //detailDataSource: undefined,
      settings: {
        data: [
          {
            id: ComponentSettingKey.VideoDataSrc,
            label: LABELS.VIDEO_URL_KEY,
            placeholder: 'Enter a valid URL',
            type: BuilderComponentSettingType.VariableSelect,
            validation: BuilderComponentSettingValidation.VideoFile,
            defaultValue: {
              id: '',
              flag: VariableSelectFeatureFlag.CustomText,
              value: 'https://www.youtube.com/watch?v=VDv4uYSAd7Q',
            },
            variableSelectSettings: {
              flags:
                VariableSelectFeatureFlag.ColumnData |
                VariableSelectFeatureFlag.CurrentRow |
                VariableSelectFeatureFlag.SingleCell |
                VariableSelectFeatureFlag.CustomText,
              columnTypes: [ColumnTypes.URL, ColumnTypes.Attachment],
            },
            variableTextSettings: {
              flags: VariableTextFeatureFlag.SingleCell | VariableTextFeatureFlag.CurrentRow,
              /*columnTypes: [ColumnTypes.URL, ColumnTypes.Attachment]*/
            },
          },
          /*{
            id: ComponentSettingKey.VideoDataTitle,
            label: 'Title',
            type: BuilderComponentSettingType.VariableText,
            variableTextSettings: {
              flags: VARIABLE_TEXT_FEATURE_FLAG_TEXT,
              columnTypes: [ColumnTypes.Text]
            }
          },*/
          {
            id: ComponentSettingKey.VideoDataCover,
            label: LABELS.COVER_IMAGE_KEY,
            type: BuilderComponentSettingType.VariableSelect,
            placeholder: 'Enter a valid URL',
            validation: BuilderComponentSettingValidation.URL,
            variableTextSettings: {
              flags:
                VariableTextFeatureFlag.SingleCell | VariableTextFeatureFlag.CurrentRow | VariableTextFeatureFlag.Image,
              /*columnTypes: [
                ColumnTypes.Image,
                ColumnTypes.Attachment,
                ColumnTypes.URL
              ]*/
            },
            variableSelectSettings: {
              flags:
                VariableSelectFeatureFlag.ColumnData |
                VariableSelectFeatureFlag.SingleCell |
                VariableSelectFeatureFlag.CurrentRow |
                VariableSelectFeatureFlag.Image |
                VariableSelectFeatureFlag.CustomText,
              columnTypes: [ColumnTypes.Image, ColumnTypes.URL, ColumnTypes.Lookup],
            },
          },
        ],
        designGroup: {
          // [LABELS.COLOR_SCHEME_KEY]: {
          //   items: [
          //     // makeScheme(ComponentSettingKey.VideoDesignScheme),
          //     makeSchemeVariant(
          //       ComponentSettingKey.VideoDesignColorVariant,
          //       ComponentSettingKey.VideoDesignScheme,
          //       true,
          //       'tertiary',
          //       'bg',
          //       LABELS.BACKGROUND_KEY
          //     ),
          //   ],
          // },
          [LABELS.CONTROLS_KEY]: {
            items: [
              // {
              //   id: ComponentSettingKey.VideoDesignProgress,
              //   label: LABELS.PROGRESS_POSITION_KEY,
              //   help: 'Only works for non embed video',
              //   type: BuilderComponentSettingType.Select,
              //   selectSettings: {
              //     options: [
              //       {
              //         value: 'progress-top',
              //         label: 'Progress Above Control',
              //       },
              //       {
              //         value: 'progress-in',
              //         label: 'Progress Inside Control',
              //       },
              //     ],
              //   },
              // },
              {
                id: ComponentSettingKey.VideoDesignControls,
                help: 'Only works for non embed video',
                label: LABELS.CONTROLS_KEY,
                type: BuilderComponentSettingType.Switch,
                defaultValue: true,
              },
              {
                id: ComponentSettingKey.VideoDesignVolume,
                help: 'Only works for non embed video',
                label: LABELS.VOLUME_CONTROL_KEY,
                type: BuilderComponentSettingType.Switch,
                defaultValue: true,
              },
            ],
          },
          Dimension: makeClampWidthItems(ComponentSettingKey.VideoDesignWidth),
          [LABELS.CORNER_RADIUS_KEY]: {
            items: [
              {
                id: ComponentSettingKey.VideoDesignShape,
                type: BuilderComponentSettingType.Select,
                label: '',
                defaultValue: MdShape.Large,
                selectSettings: {
                  options: Object.entries(MdShape).map(([label, value]) => ({
                    value,
                    label: label.replace(/([a-z])([A-Z])/g, '$1 $2'),
                  })),
                },
              },
            ],
          },
          [LABELS.SPACING_KEY]: makeSpacing({
            margin: {
              id: ComponentSettingKey.VideoDesignMargin,
              defaultValue: 10,
            },
            // padding: {
            //   id: ComponentSettingKey.VideoDesignPadding,
            //   defaultValue: 10,
            // },
          }),
          [LABELS.ALIGNMENT_KEY]: {
            items: [
              {
                id: ComponentSettingKey.VideoDesignHAlign,
                type: BuilderComponentSettingType.ComponentAlignment,
                defaultValue: 'parent',
                label: LABELS.COMPONENT_ALIGNMENT_KEY,
              },
            ],
          },
          [LABELS.ASPECT_RATIO_KEY]: {
            items: [
              {
                id: ComponentSettingKey.VideoDesignRatio,
                type: BuilderComponentSettingType.Select,
                label: '',
                defaultValue: AspectRatio.SixteenByNine,
                selectSettings: {
                  options: Object.values(AspectRatio).map((v) => {
                    return {
                      value: v,
                      label: getRatioLabel(v),
                      icon: 'AspectAuto16',
                    }
                  }),
                },
              },
            ],
          },
          [LABELS.HOVER_EFFECT_KEY]: {
            items: [
              {
                id: ComponentSettingKey.VideoDesignHoverEffect,
                type: BuilderComponentSettingType.Select,
                label: LABELS.HOVER_EFFECT_KEY,
                selectSettings: {
                  options: hoverEffectOptions,
                },
              },
            ],
          },
        },
        behavior: {
          visibility: true,
        },
      },
    },

    // audio
    {
      compId: 'audio',
      title: LABELS.AUDIO_KEY,
      searchKeywords: ['audio', 'sound', 'podcast', 'voice', 'mp3', 'music player', 'recording'],
      icon: 'Audio24',
      nested: false,
      //detailDataSource: undefined,
      settings: {
        data: [
          {
            id: ComponentSettingKey.AudioDataSrc,
            label: LABELS.AUDIO_URL_KEY,
            placeholder: 'Enter a valid URL',
            validation: BuilderComponentSettingValidation.AudioFile,
            type: BuilderComponentSettingType.VariableSelect,
            defaultValue: {
              flag: VariableSelectFeatureFlag.CustomText,
              value: 'https://storage.makyo.me/assets/default-audio.mp3',
            },
            variableSelectSettings: {
              flags:
                VariableSelectFeatureFlag.ColumnData |
                VariableSelectFeatureFlag.CurrentRow |
                VariableSelectFeatureFlag.SingleCell |
                VariableSelectFeatureFlag.File |
                VariableSelectFeatureFlag.CustomText,
              placeholder: 'Enter URL',
              columnTypes: [ColumnTypes.URL, ColumnTypes.Attachment],
            },
            variableTextSettings: {
              flags:
                VARIABLE_TEXT_FEATURE_FLAG_NO_ICON &
                ~VariableTextFeatureFlag.PageTitle &
                ~VariableTextFeatureFlag.ColumnData,
              columnTypes: [ColumnTypes.URL, ColumnTypes.Attachment],
            },
          },
          {
            id: ComponentSettingKey.AudioDataCover,
            label: LABELS.COVER_IMAGE_KEY,
            placeholder: 'Enter a valid URL',
            type: BuilderComponentSettingType.VariableSelect,
            validation: BuilderComponentSettingValidation.URL,
            variableTextSettings: {
              flags:
                VARIABLE_TEXT_FEATURE_FLAG_NO_ICON &
                ~VariableTextFeatureFlag.PageTitle &
                ~VariableTextFeatureFlag.ColumnData,
              columnTypes: [ColumnTypes.Image, ColumnTypes.Attachment],
            },
            defaultValue: {
              flag: VariableSelectFeatureFlag.Image,
              value: 'https://storage.makyo.me/assets/audio-cover.png',
            },
            variableSelectSettings: {
              flags:
                VariableSelectFeatureFlag.ColumnData |
                VariableSelectFeatureFlag.SingleCell |
                VariableSelectFeatureFlag.CurrentRow |
                VariableSelectFeatureFlag.Image |
                VariableSelectFeatureFlag.CustomText,
              columnTypes: [ColumnTypes.Image, ColumnTypes.URL, ColumnTypes.Lookup],
            },
          },
          {
            id: ComponentSettingKey.AudioDataHeader,
            label: LABELS.HEADER_KEY,
            defaultValue: [
              {
                type: 'paragraph',
                children: [{ text: 'Header' }],
              },
            ],
            type: BuilderComponentSettingType.VariableText,
            variableTextSettings: {
              flags: VARIABLE_TEXT_FEATURE_FLAG_ALL & (~VariableTextFeatureFlag.Icon & ~VariableTextFeatureFlag.Image),
              columnTypes: [
                ColumnTypes.Text,
                ColumnTypes.Template,
                ColumnTypes.Number,
                ColumnTypes.Function,
                ColumnTypes.SingleCell,
                ColumnTypes.DateTime,
                ColumnTypes.Lookup,
                ColumnTypes.Math,
              ],
            },
          },
          {
            id: ComponentSettingKey.AudioDataSubHeader,
            label: LABELS.SUB_HEADER_KEY,
            defaultValue: [
              {
                type: 'paragraph',
                children: [{ text: 'Sub Header' }],
              },
            ],
            type: BuilderComponentSettingType.VariableText,
            variableTextSettings: {
              flags: VARIABLE_TEXT_FEATURE_FLAG_ALL & (~VariableTextFeatureFlag.Icon & ~VariableTextFeatureFlag.Image),
              columnTypes: [
                ColumnTypes.Text,
                ColumnTypes.Template,
                ColumnTypes.Number,
                ColumnTypes.Function,
                ColumnTypes.SingleCell,
                ColumnTypes.DateTime,
                ColumnTypes.Lookup,
                ColumnTypes.Math,
              ],
            },
          },
          {
            id: ComponentSettingKey.AudioDataText,
            label: LABELS.SUPPORTING_TEXT_KEY,
            defaultValue: [
              {
                type: 'paragraph',
                children: [{ text: 'Supporting Text' }],
              },
            ],
            type: BuilderComponentSettingType.VariableText,
            variableTextSettings: {
              flags: VARIABLE_TEXT_FEATURE_FLAG_ALL & (~VariableTextFeatureFlag.Icon & ~VariableTextFeatureFlag.Image),
              columnTypes: [
                ColumnTypes.Text,
                ColumnTypes.Template,
                ColumnTypes.Number,
                ColumnTypes.Function,
                ColumnTypes.SingleCell,
                ColumnTypes.DateTime,
                ColumnTypes.Lookup,
                ColumnTypes.Math,
              ],
            },
          },
        ],
        designGroup: {
          [LABELS.COLOR_KEY]: {
            items: [
              // makeScheme(ComponentSettingKey.AudioDesignScheme),
              // makeSchemeVariant(
              //   ComponentSettingKey.AudioDesignColorVariant,
              //   ComponentSettingKey.AudioDesignScheme,
              //   true,
              //   'tertiary',
              //   'bg',
              //   LABELS.BACKGROUND_KEY
              // ),
              {
                id: ComponentSettingKey.AudioDesignColorVariant,
                type: BuilderComponentSettingType.ColorVariant,
                label: LABELS.BACKGROUND_KEY,
                defaultValue: 'primary-20',
              },
              // {
              //   id: ComponentSettingKey.AudioDesignOpacity,
              //   label: LABELS.TRANSPARENCY_KEY,
              //   defaultValue: [100],
              //   type: BuilderComponentSettingType.Slider,
              //   sliderSettings: {
              //     step: 1,
              //     max: 100,
              //     min: 0,
              //   },
              // },
            ],
          },
          [LABELS.DIMENSION_KEY]: makeClampWidthItems(ComponentSettingKey.AudioDesignWidth),
          [LABELS.CORNER_RADIUS_KEY]: {
            items: [
              {
                id: ComponentSettingKey.AudioDesignShape,
                type: BuilderComponentSettingType.Select,
                label: '',
                defaultValue: MdShape.Medium,
                selectSettings: {
                  options: Object.entries(MdShape).map(([label, value]) => ({
                    value,
                    label: label.replace(/([a-z])([A-Z])/g, '$1 $2'),
                  })),
                },
              },
            ],
          },
          [LABELS.SPACING_KEY]: makeSpacing({
            padding: {
              id: ComponentSettingKey.AudioDesignPadding,
              defaultValue: 10,
            },
            margin: {
              id: ComponentSettingKey.AudioDesignMargin,
            },
          }),
          [LABELS.ALIGNMENT_KEY]: {
            items: [
              {
                id: ComponentSettingKey.AudioDesignHAlign,
                type: BuilderComponentSettingType.ComponentAlignment,
                defaultValue: 'parent',
                label: LABELS.COMPONENT_ALIGNMENT_KEY,
              },
            ],
          },
          [LABELS.TYPOGRAPHY_KEY]: makeTypographyGroup(ComponentSettingKey.AudioDesignTypography, [
            {
              id: ComponentSettingKey.AudioDesignHeaderTypography,
              label: LABELS.HEADER_KEY,
              defaultValue: 'Title Medium',
            },
            {
              id: ComponentSettingKey.AudioDesignSubheaderTypography,
              label: LABELS.SUB_HEADER_KEY,
              defaultValue: 'Body Small',
            },
            {
              id: ComponentSettingKey.AudioDesignSupportingTextTypography,
              label: LABELS.SUPPORTING_TEXT_KEY,
              defaultValue: 'Body Small',
            },
            {
              id: ComponentSettingKey.AudioDesignTimerTypography,
              label: 'Timer',
              defaultValue: 'Body Small',
            },
          ]),
          [LABELS.HOVER_EFFECT_KEY]: {
            items: [
              {
                id: ComponentSettingKey.AudioDesignHoverEffect,
                type: BuilderComponentSettingType.Select,
                label: LABELS.HOVER_EFFECT_KEY,
                selectSettings: {
                  options: hoverEffectOptions,
                },
              },
            ],
          },
        },
        behavior: {
          visibility: true,
        },
      },
    },

    // array gallery
    {
      compId: 'array-gallery',
      title: LABELS.ARRAY_GALLERY_KEY,
      searchKeywords: [
        'gallery',
        'collection',
        'image list',
        'album',
        'portfolio',
        'slideshow',
        'photo gallery',
        'media gallery',
      ],
      icon: 'GalleryAlt16',
      nested: false,
      //detailDataSource: undefined,
      isDetailPageComponent: true,

      settings: {
        dataGroup: {
          [LABELS.DATA_SOURCE_KEY]: {
            items: [
              /*{
                id: ComponentSettingKey.ArrayGalleryDataUseDetailScreen,
                label: 'Use Detail Screen Data',
                type: BuilderComponentSettingType.Switch,
                defaultValue: true,
                dependOn: {
                  id: '',
                  showWhen: (_v, _b, _c, context) => {
                    return Boolean(context.page?.tableId)
                  }
                }
              },
              ,
              {
                id: ComponentSettingKey.ArrayGalleryDataSource,
                label: 'Data Source',
                type: BuilderComponentSettingType.TableSelect,
                tableSelectSettings: {
                  detailScreenDependId:
                    ComponentSettingKey.ArrayGalleryDataUseDetailScreen
                }
              }*/ //makeDataSource(ComponentSettingKey.ArrayGalleryDataSource),
              {
                id: ComponentSettingKey.ArrayGalleryDataImage,
                type: BuilderComponentSettingType.ColumnSelect,
                autoDefaultInDetailPage: true,
                label: LABELS.ITEMS_KEY,
                columnSelectSettings: {
                  tableSourceId: '',
                  //tableSourceId: ComponentSettingKey.ArrayGalleryDataSource,
                  bindDetailScreen: true,
                  columnTypes: [ColumnTypes.ImageArray],
                },
              },
              // {
              //   id: ComponentSettingKey.ArrayGalleryDataNumItems,
              //   label: 'Number of Rows',
              //   type: BuilderComponentSettingType.Number,
              //   defaultValue: 10,
              //   numberSettings: {
              //     max: 100
              //   },
              //   dependOn: {
              //     id: ComponentSettingKey.ArrayGalleryDataUseDetailScreen,
              //     showWhen: (v) => v !== true
              //   }
              // },
              // {
              //   id: ComponentSettingKey.ArrayGalleryDataLimitArrayItem,
              //   label: 'Limit Array Item',
              //   help: 'Limit number of items to show within the array.',
              //   type: BuilderComponentSettingType.Switch,
              //   defaultValue: false,
              // },
              // {
              //   id: ComponentSettingKey.ArrayGalleryDataNumArrayItems,
              //   label: 'Number of Items',
              //   type: BuilderComponentSettingType.Number,
              //   defaultValue: 10,
              //   numberSettings: {
              //     max: 100,
              //   },
              //   dependOn: {
              //     id: ComponentSettingKey.ArrayGalleryDataLimitArrayItem,
              //     showWhen: (v) => v === true,
              //   },
              // },
            ],
          },
          [LABELS.COMPONENT_TITLE_KEY]: {
            items: [
              {
                id: ComponentSettingKey.ArrayGalleryDataCompTitle,
                type: BuilderComponentSettingType.VariableText,
                label: '',
                defaultValue: [
                  {
                    type: 'paragraph',
                    children: [{ text: 'My Gallery' }],
                  },
                ],
                variableTextSettings: {
                  flags:
                    VARIABLE_TEXT_FEATURE_FLAG_ALL &
                    ~VariableTextFeatureFlag.ColumnData &
                    ~VariableTextFeatureFlag.Image,
                },
              },
            ],
          },
          /*Sorting: makeSorting(
           ComponentSettingKey.GalleryDataSortBy,
           ComponentSettingKey.GalleryDataSortType,
           ComponentSettingKey.GalleryDataSource
           ),
           'Data Filter': makeFilter(
           ComponentSettingKey.GalleryDataFilter,
           ComponentSettingKey.GalleryDataSource
           )*/
        },
        designGroup: {
          // [LABELS.COLORS_KEY]: {
          //   items: [
          //     {
          //       id: ComponentSettingKey.ArrayGalleryDesignCarouselBackgroundColor,
          //       type: BuilderComponentSettingType.ColorVariant,
          //       defaultValue: 'on-primary',
          //       label: LABELS.COMPONENT_TITLE_KEY,
          //       colorVariantSettings: {
          //         schemeId: ComponentSettingKey.ArrayGalleryDesignScheme,
          //         fullColor: 'fg',
          //       },
          //       dependOn: {
          //         id: ComponentSettingKey.ArrayGalleryDesignLayout,
          //         showWhen: (value) => value === MdGalleryLayout.Carousel,
          //       },
          //     },
          //   ],
          // },
          [LABELS.LAYOUT_KEY]: {
            items: [
              {
                id: ComponentSettingKey.ArrayGalleryDesignLayout,
                label: LABELS.LAYOUT_KEY,
                defaultValue: 'grid',
                type: BuilderComponentSettingType.Select,
                selectSettings: {
                  options: [
                    {
                      label: 'Grid',
                      value: 'grid',
                    },
                    {
                      label: 'Masonry',
                      value: 'masonry',
                    },
                    {
                      label: 'Carousel',
                      value: 'carousel',
                    },
                    // {
                    //   label: 'List',
                    //   value: 'list',
                    // },
                  ],
                },
              },
              {
                id: ComponentSettingKey.ArrayGalleryDesignCarouselArrow,
                type: BuilderComponentSettingType.Switch,
                label: LABELS.CAROUSEL_ARROW_KEY,
                defaultValue: true,
                dependOn: {
                  id: ComponentSettingKey.ArrayGalleryDesignLayout,
                  showWhen: (v) => v === MdGalleryLayout.Carousel,
                },
              },
              {
                id: ComponentSettingKey.ArrayGalleryDesignCarouselIndicator,
                type: BuilderComponentSettingType.Switch,
                label: LABELS.CAROUSEL_INDICATOR_KEY,
                defaultValue: true,
                dependOn: {
                  id: ComponentSettingKey.ArrayGalleryDesignLayout,
                  showWhen: (v) => v === MdGalleryLayout.Carousel,
                },
              },
            ],
          },
          [LABELS.DIMENSION_KEY]: {
            items: [
              {
                type: BuilderComponentSettingType.Slider,
                label: LABELS.COMPONENT_WIDTH_KEY,
                id: ComponentSettingKey.ArrayGalleryDesignWidth,
                defaultValue: [100],
                sliderSettings: {
                  max: 100,
                  min: 10,
                  step: 1,
                  range: false,
                  unit: '%',
                  showUnit: true,
                },
              },
              {
                id: ComponentSettingKey.ArrayGalleryDesignModalWidth,
                type: BuilderComponentSettingType.Slider,
                label: LABELS.MODAL_WIDTH_KEY,
                defaultValue: [100],
                clampInAllDevices: true,
                sliderSettings: {
                  max: 100,
                  min: 10,
                  step: 1,
                  range: false,
                  unit: '%',
                  showUnit: true,
                },
              },
            ],
          },
          [LABELS.ALIGNMENT_KEY]: {
            items: [
              {
                id: ComponentSettingKey.ArrayGalleryDesignCompHAlign,
                type: BuilderComponentSettingType.ComponentAlignment,
                defaultValue: 'parent',
                label: LABELS.COMPONENT_ALIGNMENT_KEY,
              },
              {
                id: ComponentSettingKey.ArrayGalleryDesignCompTitleHAlign,
                type: BuilderComponentSettingType.HorizontalAlignment,
                defaultValue: 'center',
                label: LABELS.COMPONENT_TITLE_ALIGNMENT_KEY,
              },
            ],
          },
          [LABELS.CORNER_RADIUS_KEY]: {
            items: [
              {
                id: ComponentSettingKey.ArrayGalleryDesignImageShape,
                type: BuilderComponentSettingType.Select,
                label: LABELS.IMAGE_KEY,
                defaultValue: MdShape.Medium,
                selectSettings: {
                  options: Object.entries(MdShape).map(([label, value]) => ({
                    value,
                    label: label.replace(/([a-z])([A-Z])/g, '$1 $2'),
                  })),
                },
              },
              // {
              //   id: ComponentSettingKey.ArrayGalleryDesignAvatarShape,
              //   type: BuilderComponentSettingType.Select,
              //   label: 'Avatar',
              //   defaultValue: MdShape.Full,
              //   selectSettings: {
              //     options: Object.entries(MdShape).map(([label, value]) => ({
              //       value,
              //       label: label.replace(/([a-z])([A-Z])/g, '$1 $2'),
              //     })),
              //   },
              // },
            ],
          },
          [LABELS.ASPECT_RATIO_KEY]: {
            items: [
              {
                id: ComponentSettingKey.ArrayGalleryDesignAspectRatio,
                type: BuilderComponentSettingType.Select,
                label: '',
                help: 'Will be ignored when layout variant is masonry',
                defaultValue: AspectRatio.FourByThree,
                selectSettings: {
                  options: Object.values(AspectRatio).map((v) => {
                    return {
                      value: v,
                      label: getRatioLabel(v),
                      icon: 'AspectAuto16',
                    }
                  }),
                },
              },
            ],
          },
          [LABELS.SPACING_KEY]: {
            items: [
              {
                id: ComponentSettingKey.ArrayGalleryDesignMargin,
                type: BuilderComponentSettingType.Spacing,
                label: LABELS.MARGIN_KEY,
                defaultValue: {
                  top: {
                    value: 8,
                    unit: SpacingInputUnit.Pixel,
                  },
                  bottom: {
                    value: 8,
                    unit: SpacingInputUnit.Pixel,
                  },
                  left: {
                    value: 16,
                    unit: SpacingInputUnit.Pixel,
                  },
                  right: {
                    value: 16,
                    unit: SpacingInputUnit.Pixel,
                  },
                },
                // spacingSettings: {
                //   max: {
                //     [SpacingInputUnit.Em]: 2,
                //     [SpacingInputUnit.Rem]: 2,
                //     [SpacingInputUnit.Pixel]: 32,
                //     [SpacingInputUnit.Percent]: 5,
                //   },
                // },
              },
              {
                id: ComponentSettingKey.ArrayGalleryDesignHeadlineMargin,
                type: BuilderComponentSettingType.Spacing,
                label: 'Component Title Margin',
                defaultValue: {
                  top: {
                    value: 0,
                    unit: SpacingInputUnit.Pixel,
                  },
                  bottom: {
                    value: 16,
                    unit: SpacingInputUnit.Pixel,
                  },
                  left: {
                    value: 0,
                    unit: SpacingInputUnit.Pixel,
                  },
                  right: {
                    value: 0,
                    unit: SpacingInputUnit.Pixel,
                  },
                },
                // spacingSettings: {
                //   max: {
                //     [SpacingInputUnit.Em]: 2,
                //     [SpacingInputUnit.Rem]: 2,
                //     [SpacingInputUnit.Pixel]: 32,
                //     [SpacingInputUnit.Percent]: 5,
                //   },
                // },
              },
              {
                id: ComponentSettingKey.ArrayGalleryDesignGap,
                type: BuilderComponentSettingType.Slider,
                label: LABELS.GAP_KEY,
                defaultValue: [10],
                sliderSettings: {
                  max: 100,
                  min: 0,
                },
                dependOn: {
                  id: ComponentSettingKey.ArrayGalleryDesignLayout,
                  showWhen: (value) => value === 'grid',
                },
              },
            ],
          },
          // [LABELS.TRAILING_ELEMENTS_KEY]: {
          //   items: [
          //     {
          //       id: ComponentSettingKey.ArrayGalleryDesignHideTitle,
          //       type: BuilderComponentSettingType.Switch,
          //       defaultValue: false,
          //       label: 'Hide title',
          //     },
          //   ],
          // },
          [LABELS.ITEMS_KEY]: {
            items: [
              makeNumItems(ComponentSettingKey.ArrayGalleryDataNumArrayItems),
              {
                id: ComponentSettingKey.ArrayGalleryDesignNumCols,
                label: LABELS.COLUMNS_KEY,
                type: BuilderComponentSettingType.Number,
                defaultValue: 1,
                numberSettings: {
                  max: 6,
                  min: 1,
                  step: 1,
                },
                dependOn: {
                  id: ComponentSettingKey.ArrayGalleryDesignLayout,
                  showWhen: (v) => v !== MdGalleryLayout.Carousel,
                },
              },
            ],
          },
          [LABELS.TYPOGRAPHY_KEY]: makeTypographyGroup(ComponentSettingKey.ArrayGalleryDesignTypography, [
            {
              id: ComponentSettingKey.ArrayGalleryDesignCaptionTypography,
              label: LABELS.CAPTION_KEY,
              defaultValue: 'Body Small',
            },
            {
              id: ComponentSettingKey.ArrayGalleryDesignHeadlineTypography,
              label: LABELS.COMPONENT_TITLE_KEY,
              defaultValue: 'headline-small',
            },
          ]),
        },
        action: [
          {
            id: ComponentSettingKey.ArrayGalleryActionItemClick,
            label: LABELS.ON_ITEM_CLICK_KEY,
            isSimple: true,
            actionTypes: [IActionType.ShowDetailsInModal],
          } /*,
           {
           id: ComponentSettingKey.ArrayGalleryActionSelect,
           label: 'On Image Selection',
           isSimple: false
           }*/,
        ],
        behavior: {
          /*custom: {
           'App Filters': makeUserControlledFilter(
           ComponentSettingKey.GalleryBehaviorUserFilter,
           ComponentSettingKey.GalleryDataSource
           ),
           'User Search': {
           items: [
           {
           id: ComponentSettingKey.GalleryBehaviorUserSearch,
           type: BuilderComponentSettingType.Switch,
           defaultValue: false,
           label: 'Enable User Search'
           }
           ]
           }
           },*/
          visibility: true,
        },
      },
    },
  ],
}
